// @givmo/compliance — the send review's right column: "Your authorization" and the records that
// replace its act once sending starts (CCM-R1-SUBMIT-WEB; slice 3 §5.4–§6, frames 62–72), and the
// organization's AI's (CCM-R1-SUBMIT-MCP; frames 74–77).
//
// Presentational: the page hands each card its words and its acts. Every state is said in words,
// none in color, and the left column (what goes, or what went) stays beside it.

import type { ReactNode } from 'react';
import {
  Button,
  Card,
  Dialog,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  testIdProp,
} from '@givmo/design-system';
import { serverMessage } from './compliance-decode';
import type { ReplyContactSource } from './compliance-submission-decode';
import {
  AUTHORIZE_AND_SEND,
  CANCEL,
  IT_ARRIVED,
  IT_NEVER_ARRIVED,
  KEEP_IT,
  KEPT_AS_YOUR_ACCOUNT,
  NOT_SURE_LINE,
  REVOKE,
  REVOKE_THIS_AUTHORIZATION,
  SUPPORT_FOUND_IT,
  YOUR_AI_WONT_BE_ABLE,
  type FindingDialogWords,
} from './compliance-submission-model';
import { QuietButton } from './filing-workbench-pages';
import { SourceLine } from './filing-send-parts';

const PREFIX = 'compliance-send';

/** The line a failed act says when the server gave none (the module's own). */
const TRY_AGAIN_LINE = 'Please try again.';

/** An address, or a day with its time ("Nov 18, 2026 at 10:04 AM PST"): never broken across lines. */
const KEEP_WHOLE =
  /([\w.+-]+@[\w-]+(?:\.[\w-]+)+|[A-Z][a-z]{2} \d{1,2}, \d{4} at \d{1,2}:\d{2} [AP]M(?: [A-Z]{2,4})?)/;

/** A record's line with its addresses and its day-and-time kept whole, as the frames keep them. */
export function keepWhole(line: string): ReactNode {
  return line.split(KEEP_WHOLE).map((part, index) =>
    index % 2 === 1 ? (
      <span key={index} className="whitespace-nowrap">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

// ── The card and its rows ──────────────────────────────────────────────────────────────────────

/** One label–value row, its value a stack of lines (frame 38's signature-card idiom). */
export interface CardRow {
  key: string;
  label: string;
  value: ReactNode;
}

export function CardRows({ rows }: { rows: readonly CardRow[] }) {
  return (
    <dl className="flex flex-col divide-y divide-border-subtle">
      {rows.map((row) => (
        <div
          key={row.key}
          className="grid grid-cols-[84px_minmax(0,1fr)] gap-x-12 py-10 first:pt-0 last:pb-0"
          {...testIdProp(`${PREFIX}-row-${row.key}`)}
        >
          <dt className="text-s13 text-text-tertiary">{row.label}</dt>
          <dd className="flex min-w-0 flex-col gap-2 text-s13 leading-h15 text-text">
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Frame 72's grey block at the card's top: where this environment's filing email really goes. */
export function SinkNote({ line }: { line: string }) {
  return (
    <div
      className="rounded-lg bg-surface-sunken px-16 py-12 text-s13 leading-h15 text-text"
      {...testIdProp(`${PREFIX}-sink`)}
    >
      {keepWhole(line)}
    </div>
  );
}

/**
 * The card the review's rows, its reply choice and its act sit in. `lead` is said under its title,
 * above the rows (frame 75: what the person authorized their AI to do).
 */
export function AuthorizationCard({
  title,
  sink,
  lead = [],
  children,
}: {
  title: string;
  sink?: string;
  lead?: readonly string[];
  children: ReactNode;
}) {
  return (
    <Card className="flex flex-col gap-16 px-20 py-20" testId={`${PREFIX}-authorization`}>
      {sink !== undefined && <SinkNote line={sink} />}
      <div className="flex flex-col gap-8">
        <h2 className="text-s16 font-w600 text-text-strong">{title}</h2>
        {/* Frame 75: what was authorized in body text, then the quieter line under it. */}
        {lead.map((line, index) => (
          <p
            key={line}
            className={`text-s13 leading-h15 ${index === 0 ? 'text-text' : 'text-text-secondary'}`}
            {...testIdProp(`${PREFIX}-authorization-lead`)}
          >
            {keepWhole(line)}
          </p>
        ))}
      </div>
      {children}
    </Card>
  );
}

/** A section under the rows, set off by a hairline. */
export function CardSection({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <div
      className="flex flex-col gap-10 border-t border-border-subtle pt-16"
      {...testIdProp(testId)}
    >
      {children}
    </div>
  );
}

// ── Replies and the copy ───────────────────────────────────────────────────────────────────────

export interface ReplyChoiceOption {
  source: ReplyContactSource;
  address: string;
  label: string;
}

/**
 * "Maryland’s replies and a copy of this email go to": a radio list in frame 21a's idiom where
 * there is a choice, never typed; one line where there is none, or the act is past (frame 71).
 * Under it, frame 62's letters line, where the agency's form says where its letters go.
 */
export function ReplyChoice({
  heading,
  options,
  value,
  onChange,
  fixedLine,
  note,
}: {
  heading: string;
  options: readonly ReplyChoiceOption[];
  value: ReplyContactSource | undefined;
  onChange: (source: ReplyContactSource) => void;
  /** The one-line form, when nothing is chosen here. */
  fixedLine?: string;
  /** The letters line (G3): the agency's own words, quoted. */
  note?: string;
}) {
  const letters =
    note !== undefined ? <SourceLine testId={`${PREFIX}-letters`}>{note}</SourceLine> : null;
  if (fixedLine !== undefined) {
    return (
      <CardSection testId={`${PREFIX}-reply`}>
        <p className="text-s13 leading-h15 text-text">{fixedLine}</p>
        {letters}
      </CardSection>
    );
  }
  return (
    <CardSection testId={`${PREFIX}-reply`}>
      <p className="text-s13 font-w600 leading-h15 text-text-strong">{heading}</p>
      <RadioTileGroup
        value={value ?? options[0]?.source ?? ''}
        onValueChange={(next) => onChange(next as ReplyContactSource)}
        aria-label={heading}
        className="flex flex-col gap-10"
      >
        {options.map((option) => (
          <RadioTile
            key={option.source}
            value={option.source}
            label={option.address}
            description={option.label}
            testId={`${PREFIX}-reply-${option.source}`}
          />
        ))}
      </RadioTileGroup>
      {letters}
    </CardSection>
  );
}

// ── The act, and what stands in its place ──────────────────────────────────────────────────────

/**
 * The act's words directly above **Authorize and send**, and under a disabled button the served
 * line that says why (frame 63). A refusal of the act itself is said under it in the server's words.
 * An account that can't meet the organization's sign-in check here reads W1 (`notice`) above the
 * act's words, and the host's offer to set up a passkey stands in the button's place while it is open
 * (`inPlaceOfAct`; CCM-R1-SIGN-PASSKEY).
 */
export function ActSection({
  statement,
  disabled,
  pending,
  holds,
  failure,
  onAct,
  after,
  notice,
  inPlaceOfAct,
  heading,
  lines = [],
  label = AUTHORIZE_AND_SEND,
  actTestId = `${PREFIX}-authorize`,
}: {
  statement: string;
  disabled: boolean;
  pending: boolean;
  holds: readonly string[];
  failure: unknown;
  onAct: () => void;
  /** Under the act and its lines: "Send it yourself instead" (frame 62). */
  after?: ReactNode;
  /** Above the act: why the sign-in check can't be met here (W1). */
  notice?: string;
  /** Drawn in the button's place while given: the host's passkey setup offer. */
  inPlaceOfAct?: ReactNode;
  /** Frame 74's act block: its heading, and its lines above the act's words. */
  heading?: string;
  lines?: readonly string[];
  /** The act's label: Authorize and send, or frame 74's Authorize my AI to send it. */
  label?: string;
  actTestId?: string;
}) {
  return (
    <CardSection testId={`${PREFIX}-act`}>
      {heading !== undefined && (
        <p
          className="text-s14 font-w600 leading-h15 text-text-strong"
          {...testIdProp(`${PREFIX}-act-heading`)}
        >
          {heading}
        </p>
      )}
      {/* Frame 74: what the AI can do in body text, then when it ends, quieter. */}
      {lines.map((line, index) => (
        <p
          key={line}
          className={`text-s13 leading-h15 ${index === 0 ? 'text-text' : 'text-text-secondary'}`}
          {...testIdProp(`${PREFIX}-act-line`)}
        >
          {keepWhole(line)}
        </p>
      ))}
      {/* Above the act's words, as S10 sits above the signing statement: the words stay directly
          over the act they authorize. */}
      {notice !== undefined && (
        <p
          className="text-s13 leading-h15 text-text"
          {...testIdProp(`${PREFIX}-cannot-meet-check`)}
        >
          {notice}
        </p>
      )}
      <p
        className={`text-s14 font-w600 leading-h15 text-text-strong${lines.length > 0 ? ' pt-4' : ''}`}
        {...testIdProp(`${PREFIX}-statement`)}
      >
        {statement}
      </p>
      {inPlaceOfAct !== undefined ? (
        <div {...testIdProp(`${PREFIX}-passkey-offer`)}>{inPlaceOfAct}</div>
      ) : (
        <Button
          appearance="saas"
          tone="primary"
          size="lg"
          fullWidth
          disabled={disabled}
          loading={pending}
          onClick={onAct}
          testId={actTestId}
        >
          {label}
        </Button>
      )}
      {holds.map((line) => (
        <p
          key={line}
          className="text-s13 leading-h15 text-text-secondary"
          {...testIdProp(`${PREFIX}-hold`)}
        >
          {line}
        </p>
      ))}
      {failure !== null && failure !== undefined && (
        <ErrorMessage testId={`${PREFIX}-act-failure`}>
          {serverMessage(failure, TRY_AGAIN_LINE)}
        </ErrorMessage>
      )}
      {after}
    </CardSection>
  );
}

/** Frame 65, in the card while the send is under way: no act, the button busy. */
export function SendingSection({
  title,
  line,
  busyLabel,
}: {
  title: string;
  line: string;
  busyLabel: string;
}) {
  return (
    <CardSection testId={`${PREFIX}-sending`}>
      <p className="text-s14 font-w600 leading-h15 text-text-strong">{title}</p>
      <p className="text-s13 leading-h15 text-text-secondary">{line}</p>
      <Button appearance="saas" tone="primary" size="lg" fullWidth loading>
        {busyLabel}
      </Button>
    </CardSection>
  );
}

/** Frame 71: a Member can't authorize, and is told who can. */
export function MemberSection({
  line,
  after,
}: {
  line: string;
  /** Under the line: "Send it yourself instead" (frame 71), as under the act (frame 62). */
  after?: ReactNode;
}) {
  return (
    <CardSection>
      <p
        className="text-s13 leading-h15 text-text-secondary"
        {...testIdProp(`${PREFIX}-owner-line`)}
      >
        {line}
      </p>
      {after}
    </CardSection>
  );
}

/**
 * The card's acts once authorized and not yet sent, or sent and refused, or recorded as not sent:
 * the primary act (Send it, Try again) and a person's Revoke in one click (the GO's Q5).
 */
export function SendAgainActs({
  lines = [],
  label,
  canSend,
  canRevoke,
  sending,
  revoking,
  failure,
  onSend,
  onRevoke,
  after,
}: {
  /** Lines over the acts (M4: when the authorization runs out). */
  lines?: readonly string[];
  label: string;
  canSend: boolean;
  canRevoke: boolean;
  sending: boolean;
  revoking: boolean;
  failure: unknown;
  onSend: () => void;
  onRevoke: () => void;
  /** A quiet line under the acts (frame 68's support line). */
  after?: string;
}) {
  if (!canSend && !canRevoke && after === undefined && lines.length === 0) return null;
  return (
    <CardSection testId={`${PREFIX}-again`}>
      {lines.map((line) => (
        <p key={line} className="text-s13 leading-h15 text-text-secondary">
          {line}
        </p>
      ))}
      {canSend && (
        <Button
          appearance="saas"
          tone="primary"
          size="lg"
          fullWidth
          loading={sending}
          disabled={revoking}
          onClick={onSend}
          testId={`${PREFIX}-send-again`}
        >
          {label}
        </Button>
      )}
      {canRevoke && (
        <div>
          <QuietButton
            onClick={revoking || sending ? () => undefined : onRevoke}
            testId={`${PREFIX}-revoke`}
          >
            {REVOKE}
          </QuietButton>
        </div>
      )}
      {failure !== null && failure !== undefined && (
        <ErrorMessage testId={`${PREFIX}-again-failure`}>
          {serverMessage(failure, TRY_AGAIN_LINE)}
        </ErrorMessage>
      )}
      {after !== undefined && <SourceLine>{after}</SourceLine>}
    </CardSection>
  );
}

// ── The records (frames 66, 68, 69, 70) ────────────────────────────────────────────────────────

export function RecordCard({
  title,
  children,
  testId,
}: {
  title: string;
  children: ReactNode;
  testId: string;
}) {
  return (
    <Card className="flex flex-col gap-14 px-20 py-20" testId={testId}>
      <h2 className="text-s16 font-w600 text-text-strong">{title}</h2>
      {children}
    </Card>
  );
}

/** A record's opening lines: the first in body size, the rest a shade quieter. */
export function RecordLines({ first, rest }: { first: string; rest: readonly string[] }) {
  return (
    <div className="-mt-4 flex flex-col gap-8">
      <p className="text-s14 leading-h15 text-text" {...testIdProp(`${PREFIX}-record-first`)}>
        {keepWhole(first)}
      </p>
      {rest.map((line) => (
        <p key={line} className="text-s13 leading-h15 text-text-secondary">
          {keepWhole(line)}
        </p>
      ))}
    </div>
  );
}

/**
 * Frame 66's "What happens next", with Back to the filing; after a send, the step's own acts lead,
 * in the same row (REVIEW-30's a1–a8).
 */
export function WhatHappensNext({
  title,
  line,
  back,
  onBack,
  acts,
}: {
  title: string;
  line: string;
  back: string;
  onBack: () => void;
  acts?: ReactNode;
}) {
  return (
    <div
      className="flex flex-col gap-6 border-t border-border-subtle pt-14"
      {...testIdProp(`${PREFIX}-next`)}
    >
      <p className="text-s14 font-w600 text-text-strong">{title}</p>
      <p className="text-s13 leading-h15 text-text" {...testIdProp(`${PREFIX}-next-line`)}>
        {line}
      </p>
      <div className="flex flex-wrap gap-8 pt-4">
        {acts}
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          onClick={onBack}
          testId={`${PREFIX}-back`}
        >
          {back}
        </Button>
      </div>
    </div>
  );
}

/** Frame 66's sending record: what Givmo sent, to whom and when, and what it doesn't show. */
export function SendingRecord({
  title,
  lines,
  quiet,
  denial,
}: {
  title: string;
  lines: readonly string[];
  quiet: readonly string[];
  denial: string;
}) {
  return (
    <div
      className="flex flex-col gap-8 border-t border-border-subtle pt-14"
      {...testIdProp(`${PREFIX}-sending-record`)}
    >
      <p className="text-s14 font-w600 text-text-strong">{title}</p>
      <ul className="flex flex-col gap-6 text-s13 leading-h15 text-text">
        {lines.map((line) => (
          <li key={line}>{keepWhole(line)}</li>
        ))}
        {quiet.map((line) => (
          <li key={line} className="break-words text-text-secondary">
            {keepWhole(line)}
          </li>
        ))}
      </ul>
      <SourceLine testId={`${PREFIX}-sending-record-denial`}>{denial}</SourceLine>
    </div>
  );
}

/**
 * Frame 69: the question, and the three findings a Compliance Owner or a Global Admin can record,
 * each confirmed in its dialog; a Member reads the question and who can answer it (N6).
 */
export function UncertainQuestion({
  question,
  hint,
  canRecord,
  memberLine,
  onFinding,
}: {
  question: string;
  hint: string;
  canRecord: boolean;
  memberLine: string;
  onFinding: (basis: 'copy_received' | 'provider_activity' | 'not_received') => void;
}) {
  return (
    <div className="flex flex-col gap-10 border-t border-border-subtle pt-14">
      <p
        className="text-s14 font-w600 leading-h15 text-text-strong"
        {...testIdProp(`${PREFIX}-question`)}
      >
        {question}
      </p>
      <SourceLine>{hint}</SourceLine>
      {canRecord ? (
        <div className="grid grid-cols-2 gap-8 pt-2">
          <Button
            appearance="saas"
            tone="secondary"
            size="md"
            fullWidth
            onClick={() => onFinding('copy_received')}
            testId={`${PREFIX}-it-arrived`}
          >
            {IT_ARRIVED}
          </Button>
          <Button
            appearance="saas"
            tone="secondary"
            size="md"
            fullWidth
            onClick={() => onFinding('not_received')}
            testId={`${PREFIX}-it-never-arrived`}
          >
            {IT_NEVER_ARRIVED}
          </Button>
        </div>
      ) : (
        <p
          className="text-s13 leading-h15 text-text-secondary"
          {...testIdProp(`${PREFIX}-record-owner-line`)}
        >
          {memberLine}
        </p>
      )}
      <SourceLine>{NOT_SURE_LINE}</SourceLine>
      {canRecord && (
        <div>
          <QuietButton
            onClick={() => onFinding('provider_activity')}
            testId={`${PREFIX}-support-found-it`}
          >
            {SUPPORT_FOUND_IT}
          </QuietButton>
        </div>
      )}
    </div>
  );
}

/**
 * Frame 77's box: when the person authorized it, what they asked their AI as the AI reported it,
 * and that Givmo keeps that as the organization's own account (CCM-R1-SUBMIT-MCP).
 */
export function AiAskedBox({ authorized, asked }: { authorized: string; asked: string }) {
  return (
    <div
      className="flex flex-col gap-8 rounded-lg bg-surface-sunken px-16 py-12"
      {...testIdProp(`${PREFIX}-ai-asked`)}
    >
      <p className="text-s13 leading-h15 text-text">{keepWhole(authorized)}</p>
      <p className="break-words text-s13 leading-h15 text-text">{asked}</p>
      <SourceLine>{KEPT_AS_YOUR_ACCOUNT}</SourceLine>
    </div>
  );
}

/**
 * Frame 76: revoking an authorization the person's AI may still send. Revoke takes the authority
 * away; Keep it closes the dialog. Nothing is sent either way.
 */
export function RevokeForAiDialog({
  open,
  pending,
  failure,
  onConfirm,
  onClose,
}: {
  open: boolean;
  pending: boolean;
  failure: unknown;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) onClose();
      }}
    >
      {open && (
        <Dialog.Content
          width={440}
          className="flex flex-col gap-16"
          testId={`${PREFIX}-revoke-dialog`}
        >
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {REVOKE_THIS_AUTHORIZATION}
          </Dialog.Title>
          <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {YOUR_AI_WONT_BE_ABLE}
          </Dialog.Description>
          {failure !== null && failure !== undefined && (
            <ErrorMessage testId={`${PREFIX}-revoke-failure`}>
              {serverMessage(failure, TRY_AGAIN_LINE)}
            </ErrorMessage>
          )}
          <div className="flex flex-wrap gap-8">
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              loading={pending}
              onClick={onConfirm}
              testId={`${PREFIX}-revoke-confirm`}
            >
              {REVOKE}
            </Button>
            <Button
              appearance="saas"
              tone="ghost"
              size="md"
              disabled={pending}
              onClick={onClose}
              testId={`${PREFIX}-revoke-keep`}
            >
              {KEEP_IT}
            </Button>
          </div>
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
}

/** One finding's dialog (frame 69b's idiom): its act, then Cancel. Nothing is sent again. */
export function FindingDialog({
  words,
  pending,
  failure,
  onConfirm,
  onClose,
}: {
  words: FindingDialogWords | undefined;
  pending: boolean;
  failure: unknown;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const open = words !== undefined;
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) onClose();
      }}
    >
      {words !== undefined && (
        <Dialog.Content width={440} className="flex flex-col gap-16" testId={`${PREFIX}-finding`}>
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {words.title}
          </Dialog.Title>
          <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {words.body}
          </Dialog.Description>
          {failure !== null && failure !== undefined && (
            <ErrorMessage testId={`${PREFIX}-finding-failure`}>
              {serverMessage(failure, TRY_AGAIN_LINE)}
            </ErrorMessage>
          )}
          <div className="flex flex-wrap gap-8">
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              loading={pending}
              onClick={onConfirm}
              testId={`${PREFIX}-finding-confirm`}
            >
              {words.confirm}
            </Button>
            <Button
              appearance="saas"
              tone="ghost"
              size="md"
              disabled={pending}
              onClick={onClose}
              testId={`${PREFIX}-finding-cancel`}
            >
              {CANCEL}
            </Button>
          </div>
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
}
